import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X, User, LogOut, Users, ShoppingCart } from 'lucide-react'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'
import { useAdminAuth } from '../contexts/AdminAuthContext'
import ThemeToggle from './ThemeToggle'

const Header = () => {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { customer, isAuthenticated: customerAuth, logout: customerLogout } = useCustomerAuth()
  const { admin, isAuthenticated: adminAuth, logout: adminLogout } = useAdminAuth()

  const handleLogout = () => {
    if (customerAuth) {
      customerLogout()
      navigate('/customer/login')
    } else if (adminAuth) {
      adminLogout()
      navigate('/admin/login')
    }
    setIsOpen(false)
  }

  const getLogoLink = () => {
    if (customerAuth) return '/customer/dashboard'
    if (adminAuth) return '/admin/dashboard'
    return '/customer/login'
  }

  const isActive = (path) => {
    return location.pathname === path
  }

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to={getLogoLink()} className="flex items-center space-x-2">
              <img 
                src="/images/UFCC_Logo.png" 
                alt="UFCC Logo" 
                className="h-8 w-8 object-contain"
              />
              <span className="text-xl font-bold text-gray-900">UFCC Enterprise</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {!customerAuth && !adminAuth && (
              <>
                <Link
                  to="/customer/login"
                  className={`text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/customer/login') ? 'text-primary-600' : ''
                  }`}
                >
                  Member Login
                </Link>
                <Link
                  to="/admin/login"
                  className={`text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/login') ? 'text-primary-600' : ''
                  }`}
                >
                  MG Admin
                </Link>
              </>
            )}

            {customerAuth && (
              <>
                <Link
                  to="/customer/dashboard"
                  className={`text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/customer/dashboard') ? 'text-primary-600' : ''
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/customer/financial-data"
                  className={`text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/customer/financial-data') ? 'text-primary-600' : ''
                  }`}
                >
                  Financial Data
                </Link>
                <Link
                  to="/katale"
                  className={`text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium flex items-center ${
                    location.pathname.startsWith('/katale') ? 'text-primary-600' : ''
                  }`}
                >
                  <ShoppingCart className="h-4 w-4 mr-1" />
                  MG Katale
                </Link>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    Welcome, {customer?.fullName || customer?.client_name || customer?.name || customer?.username || customer?.memberNo}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-gray-900 p-2 rounded-md"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>
              </>
            )}

            {adminAuth && (
              <>
                <Link
                  to="/admin/dashboard"
                  className={`text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/dashboard') ? 'text-primary-600' : ''
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/admin/customers"
                  className={`text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium ${
                    isActive('/admin/customers') ? 'text-primary-600' : ''
                  }`}
                >
                  Customers
                </Link>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    Admin: {admin?.username}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-gray-900 p-2 rounded-md"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>
              </>
            )}
            <ThemeToggle />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-1">
            <ThemeToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-gray-900 p-2 rounded-md"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
            {!customerAuth && !adminAuth && (
              <>
                <Link
                  to="/customer/login"
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsOpen(false)}
                >
                  Member Login
                </Link>
                <Link
                  to="/admin/login"
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsOpen(false)}
                >
                  MG Admin
                </Link>
              </>
            )}

            {customerAuth && (
              <>
                <Link
                  to="/customer/dashboard"
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/customer/financial-data"
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsOpen(false)}
                >
                  Financial Data
                </Link>
                <Link
                  to="/katale"
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md flex items-center"
                  onClick={() => setIsOpen(false)}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  MG Katale
                </Link>
                <div className="px-3 py-2 text-base font-medium text-gray-600 border-t border-gray-200">
                  Welcome, {customer?.fullName || customer?.client_name || customer?.name || customer?.username || customer?.memberNo}
                </div>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                >
                  Logout
                </button>
              </>
            )}

            {adminAuth && (
              <>
                <Link
                  to="/admin/dashboard"
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/admin/customers"
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsOpen(false)}
                >
                  Customers
                </Link>
                <div className="px-3 py-2 text-base font-medium text-gray-600 border-t border-gray-200">
                  Admin: {admin?.username}
                </div>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}

export default Header
